import { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation } from 'react-router-dom';

import Loading from 'components/global/Loading';
import { settingsSidebarConfig, sideBarConfig } from 'components/layout/SideBar/SideBarConfig';
import NotFoundSms from 'components/templates/NotFoundSms';
import SmsLayout from 'components/templates/SmsLayout';
import SmsLinkExpired from 'components/templates/SmsLinkExpired';

import APIIntegrations from 'pages/APIIntegrations/APIIntegrations';
import ConnectedTokens from 'pages/APIIntegrations/ConnectedTokens';
import OtoAPI from 'pages/APIIntegrations/OtoAPI';
import AccessControl from 'pages/Admin/AccessControl/AccessControl';
import ClientListing from 'pages/Admin/ClientListing/ClientListing';
import CohortAggregatorAnalysis from 'pages/Admin/CohortAnalysis/CohortAggregatorAnalysis';
import CohortAnalysis from 'pages/Admin/CohortAnalysis/CohortAnalysis';
import CohortIntegratorAnalysis from 'pages/Admin/CohortAnalysis/CohortIntegratorAnalysis';
import CountriesManagementList from 'pages/Admin/CountriesManagement/CountriesManagementList';
import DeliveryCompanyModule from 'pages/Admin/DeliveryCompanyModule/DeliveryCompanyModule';
import DeliveryCompanySettings from 'pages/Admin/DeliveryCompanyModule/DeliveryCompanySettings/DeliveryCompanySettings';
import PickupCities from 'pages/Admin/DeliveryCompanyModule/PickupCities/PickupCities';
import PolygonCoverage from 'pages/Admin/DeliveryCompanyModule/PolygonCoverage/PolygonCoverage';
import EnumManagement from 'pages/Admin/EnumManagement/EnumManagement';
import HomePageAnnouncements from 'pages/Admin/HomePageAnnouncementsManagement/HomePageAnnouncements';
import LanguageResourceManagement from 'pages/Admin/LanguageResourceManagement/LanguageResourceManagement';
import ModulesList from 'pages/Admin/Modules/ModuleList';
import OTOAIClientConfigurations from 'pages/Admin/OTOAIClientConfigurations/OTOAIClientConfigurations';
import OtherIntegrationsList from 'pages/Admin/OtherIntegrations/OtherIntegrationsList';
import Packages from 'pages/Admin/Packages/Packages';
import PackagesList from 'pages/Admin/Packages/PackagesList';
import RoleTree from 'pages/Admin/RoleTree/RoleTree';
import SalesChannelsList from 'pages/Admin/SalesChannels/SalesChannelsList';
import StoryManagementList from 'pages/Admin/StoryManagement/StoryManagementList';
import SubscriptionListing from 'pages/Admin/Subcriptions/SubscriptionListing';
import SubscriptionTransactionsListing from 'pages/Admin/Subcriptions/SubscriptionTransactionsListing';
import Subscriptions from 'pages/Admin/Subcriptions/Subscriptions';
import Analysis from 'pages/Analysis/Analysis';
import CustomerDetailsTab from 'pages/Analysis/CustomerDetailsTab/CustomerDetails';
import DeliveryCompanyTab from 'pages/Analysis/DeliveryCompanyTab/DeliveryCompany';
import OTOFlexTab from 'pages/Analysis/OTOFlexTab/OTOFlex';
import OTOPackTab from 'pages/Analysis/OTOPackTab/OTOPackTab';
import Overview from 'pages/Analysis/Overview/Overview';
import SLAPerformance from 'pages/Analysis/SLAPerformanceTab/SLAPerformance';
import SalesTab from 'pages/Analysis/SalesTab/Sales';
import ShippingAccountingTab from 'pages/Analysis/ShippingAccountingTab/ShippingAccounting';
import AutomationList from 'pages/Automation/AutomationList';
import BRCanceledOrderList from 'pages/BranchUser/BRCanceled/BRCanceledOrderList';
import BRDashboard from 'pages/BranchUser/BRDashboard/BRDashboard';
import BRDeliveredOrderList from 'pages/BranchUser/BRDelivered/BRDeliveredOrderList';
import BRPendingOrderList from 'pages/BranchUser/BRPendingOrders/BRPendingOrderList';
import BRReturnedOrderList from 'pages/BranchUser/BRReturned/BRReturnedOrderList';
import BRShipmentInProgressList from 'pages/BranchUser/BRShipmentInProgress/BRShipmentInProgressList';
import BRUnassignedOrderList from 'pages/BranchUser/BRUnassigned/BRUnassignedOrderList';
import BRWaitingForDriverList from 'pages/BranchUser/BRWaitingForDriver/BRWaitingForDriverList';
import BranchList from 'pages/Branches/BranchList';
import HistoryOrdersList from 'pages/CCModule/HistoryOrders/HistoryOrdersList';
import ProcessingOrdersList from 'pages/CCModule/ProcessingOrders/ProcessingOrdersList';
import CustomerService from 'pages/CSModule/CustomerService/CustomerService';
import CustomerServiceFeedbacksList from 'pages/CSModule/CustomerService/CustomerServiceFeedbacks/CustomerServiceFeedbacksList';
import FailedDeliveryOrdersList from 'pages/CSModule/CustomerService/FailedDeliveryOrders/FailedDeliveryOrdersList';
import CompanyKPIDashboard from 'pages/CompanyKPIDashboard/CompanyKPIDashboard';
import Dashboard from 'pages/Dashboard/Dashboard';
import DashboardMonitor from 'pages/Dashboard/DashboardMonitor';
import ErrorPage from 'pages/Errors/ErrorPage';
import PageNotFound from 'pages/Errors/PageNotFound';
import ExternalPrint from 'pages/ExternalPrint/ExternalPrint';
import BankTransferList from 'pages/FinanceUser/BankTransfer/BankTransferList';
import Banks from 'pages/FinanceUser/Banks/BankList';
import ApprovedCODReportList from 'pages/FinanceUser/CODPayable/ApprovedCODReports/ApprovedCODReportList';
import CODPayable from 'pages/FinanceUser/CODPayable/CODPayable/CODPayable';
import CashDepositedReportList from 'pages/FinanceUser/CODPayable/CashDepositedReports/CashDepositedReportList';
import NewCODReportsList from 'pages/FinanceUser/CODPayable/NewCODReports/NewCODReportList';
import CODReceivables from 'pages/FinanceUser/CODReceivables/CODReceivables';
import NewReceivableReports from 'pages/FinanceUser/CODReceivables/NewReceivableReports/NewReceivableReports';
import NoConflict from 'pages/FinanceUser/CODReceivables/NoConflict/NoConflict';
import ReceivedOTOBankFromDC from 'pages/FinanceUser/CODReceivables/ReceivedOTOBankFromDC/ReceivedOTOBankFromDC';
import ReconciledOrders from 'pages/FinanceUser/CODReceivables/ReconciledOrders/ReconciledOrders';
import WaitingReconciliationOrders from 'pages/FinanceUser/CODReceivables/WaitingReconciliationOrders/WaitingReconciliationOrders';
import AllWithdrawalsList from 'pages/FinanceUser/CODWalletTransactions/AllWithdrawals/AllWithdrawals';
import BankTransferFailedList from 'pages/FinanceUser/CODWalletTransactions/BankTransferFailed/BankTransferFailed';
import BankTransferProcessingList from 'pages/FinanceUser/CODWalletTransactions/BankTransferProcessing/BankTransferProcessing';
import CODWalletTransactions from 'pages/FinanceUser/CODWalletTransactions/CODWalletTransactions/CODWalletTransactions';
import ClientTransactionList from 'pages/FinanceUser/ClientTransactions/ClientTransactionsList';
import RefundTransactionsList from 'pages/FinanceUser/ClientTransactions/RefundTransactionsList';
import Transactions from 'pages/FinanceUser/ClientTransactions/Transactions';
import ExtraCredits from 'pages/FinanceUser/ExtraCredits/ExtraCredits';
import ExtraCreditForEnterprise from 'pages/FinanceUser/ExtraCredits/ExtraCreditsForEnterpriseList';
import ExtraCreditForSmb from 'pages/FinanceUser/ExtraCredits/ExtraCreditsForSMBList';
import InvoiceLogs from 'pages/FinanceUser/InvoiceLogs/InvoiceLogs';
import OTOInvoiceLogs from 'pages/FinanceUser/InvoiceLogs/OTOInvoiceLogs';
import OTOLinkTransactionList from 'pages/FinanceUser/OTOLink/OTOLinkTransactionList';
import UnitEconomicsDashboard from 'pages/FinanceUser/UnitEconomicsDashboard/UnitEconomicsDashboard';
import DriverHome from 'pages/Home/DriverHome';
import Home from 'pages/Home/Home';
import ImportOrders from 'pages/ImportOrders/ImportOrdersList';
import AppIntegrationFrame from 'pages/Login/AppIntegrationFrame';
import ChangePassword from 'pages/Login/ChangePassword';
import ForgotPassword from 'pages/Login/ForgotPassword';
import Login from 'pages/Login/Login';
import Register from 'pages/Login/Register';
import RegisterWithProvider from 'pages/Login/RegisterWithProvider';
import Logout from 'pages/Logout/Logout';
import AutomationErrorLogList from 'pages/Logs/AutomationErrorLogs/AutomationErrorLogList';
import AutomationSuccessLogList from 'pages/Logs/AutomationSuccessLogs/AutomationSuccessLogList';
import EmailLogList from 'pages/Logs/EmailLogs/EmailLogList';
import IntegrationLogs from 'pages/Logs/IntegrationLogs';
import InventorySyncLogList from 'pages/Logs/InventorySyncLogs/InventorySyncLogList';
import NotificationLogs from 'pages/Logs/NotificationLogs';
import PickUpRequestLogList from 'pages/Logs/PickUpRequestLogs/PickUpRequestLogList';
import ProductSyncLogList from 'pages/Logs/ProductSyncLogs';
import SMSLogList from 'pages/Logs/SMSLogs/SMSLogList';
import ShipmentErrorLogList from 'pages/Logs/ShipmentErrorLogs/ShipmentErrorLogList';
import SystemLogs from 'pages/Logs/SystemLogs';
import UpdateErrorLogList from 'pages/Logs/UpdateErrorLogs/UpdateErrorLogList';
import UpdateSuccessLogList from 'pages/Logs/UpdateSuccessLogs/UpdateSuccessLogList';
import WebhookErrorLogList from 'pages/Logs/WebhookErrorLogs/WebhookErrorLogList';
import BMInventoryList from 'pages/MultiBrandModule/BMInventory/BMInventoryList';
import BrandOrders from 'pages/MultiBrandModule/BMOrders/BMOrders';
import BMProductList from 'pages/MultiBrandModule/BMProduct/BMProductList';
import DriverList from 'pages/OTOFlexMain/Drivers/DriverList';
import HistoryOrderList from 'pages/OTOFlexMain/HistoryOrders/HistoryOrderList';
import OTOFlex from 'pages/OTOFlexMain/OTOFlex';
import ProcessingOrderList from 'pages/OTOFlexMain/ProcessingOrders/ProcessingOrderList';
import UnassignedOrderList from 'pages/OTOFlexMain/UnassignedOrders/UnassignedOrderList';
import OTOFlexTracking from 'pages/OTOFlexTracking/OTOFlexTracking';
import ActiveLinks from 'pages/OTOLink/ActiveLinks';
import AllLinks from 'pages/OTOLink/AllLinks';
import OTOLink from 'pages/OTOLink/OTOLink';
import OTOLinkBuyer from 'pages/OTOLink/OTOLinkBuyer/OTOLinkBuyer';
import OTOLinkOrders from 'pages/OTOLink/OTOLinkOrders';
import ModuleDetail from 'pages/OTOModules/ModuleDetail/ModuleDetail';
import OTOModules from 'pages/OTOModules/OTOModules';
import OTOStore from 'pages/OTOStore/OTOStore';
import OTOStoreCheckout from 'pages/OTOStore/OTOStoreCheckout';
import OTOStoreMyOrders from 'pages/OTOStore/OTOStoreMyOrders';
import OTOStoreProductDetails from 'pages/OTOStore/OTOStoreProductDetails';
import OrderOtomation from 'pages/OrderOtomation/OrderOtomationList';
import ConnectedIntegrations from 'pages/OtherIntegrations/ConnectedIntegrations/ConnectedIntegrations';
import Integrations from 'pages/OtherIntegrations/Integrations/Integrations';
import Navigation from 'pages/OtherIntegrations/OtherIntegrations';
import PriceCalculator from 'pages/PriceCalculator/PriceCalculator';
import PriceCalculatorWeb from 'pages/PriceCalculatorWeb/PriceCalculatorWeb';
import BulletDistrictSLAList from 'pages/PriceConfigurations/BulletDistrictSLA/BulletDistrictSLAList';
import BulletKMSLAList from 'pages/PriceConfigurations/BulletKMSLA/BulletKMSLAList';
import CityBasedPricesList from 'pages/PriceConfigurations/CityBasedPrices/CityBasedPriceList';
import CityBasedSLAList from 'pages/PriceConfigurations/CityBasedSLA/CityBasedSLAList';
import ClosedShipmentList from 'pages/PriceConfigurations/ClosedShipments/ClosedShipmentList';
import CountryBasedSLAList from 'pages/PriceConfigurations/CountryBasedSLA/CountryBasedSLAList';
import ContractConfigurations from 'pages/PriceConfigurations/DeliveryPriceSystem/ContractConfigurations';
import InvoiceChecker from 'pages/PriceConfigurations/DeliveryPriceSystem/InvoiceChecker';
import PriceConfigurations from 'pages/PriceConfigurations/DeliveryPriceSystem/PriceConfigurations';
import SLAConfigurations from 'pages/PriceConfigurations/DeliveryPriceSystem/SLAConfigurations';
import DistrictBasedSLAList from 'pages/PriceConfigurations/DistrictBasedSLA/DistrictBasedSLAList';
import KMBasedPriceList from 'pages/PriceConfigurations/KMBasedPrices/KMBasedPriceList';
import KMBasedSLAList from 'pages/PriceConfigurations/KMBasedSLA/KMBasedSLAList';
import OTODeliveryPricesList from 'pages/PriceConfigurations/OTODeliveryPrices/OTODeliveryPriceList';
import OTOMarginList from 'pages/PriceConfigurations/OTOMargin/OTOMarginList';
import ReconciliationList from 'pages/PriceConfigurations/Reconciliation/ReconciliationList';
import ShippingContractList from 'pages/PriceConfigurations/ShippingContracts/ShippingContractList';
import SpecialContractTermsList from 'pages/PriceConfigurations/SpecialContractTerms/SpecialContractTermList';
import SuccessInstructionsList from 'pages/PriceConfigurations/SuccessInstructions/SuccessInstructionsList';
import TierBasedPriceList from 'pages/PriceConfigurations/TierBasedPrices/TierBasedPriceList';
import TierBasedSLAList from 'pages/PriceConfigurations/TierBasedSLA/TierBasedSLAList';
import WeightBasedPriceList from 'pages/PriceConfigurations/WeightBasedPrices/WeightBasedPriceList';
import WeightCorrClosed from 'pages/PriceConfigurations/WeightCorrClosed/WeightCorrClosed';
import WeightCorrOpen from 'pages/PriceConfigurations/WeightCorrOpen/WeightCorrOpen';
import WeightCountryBasedPriceList from 'pages/PriceConfigurations/WeightCountryBasedPrices/WeightCountryBasedPriceList';
import AttributesGroupsList from 'pages/Products/AttributesGroups/AttributesGroupsList';
import CategoriesList from 'pages/Products/Categories/CategoriesList';
import Product from 'pages/Products/Product';
import ProductAttributesList from 'pages/Products/ProductAttributes/ProductAttributesList';
import ProductList from 'pages/Products/Products/ProductList';
import ReturnsList from 'pages/RMSModule/Returns/ReturnsList';
import BranchDailyStaffReport from 'pages/Reports/BranchReports/BranchDailyStaffReport/BranchDailyStaffReport';
import BranchReport from 'pages/Reports/BranchReports/BranchReport/BranchReport';
import BranchReports from 'pages/Reports/BranchReports/BranchReports';
import FoodicsShippingTransactionReport from 'pages/Reports/FoodicsShippingTransaction/FoodicsShippingTransactionReport';
import DriverDetails from 'pages/Reports/OTOFlexReports/DriverDetailsReport/DriverDetailsReport';
import DriverSummary from 'pages/Reports/OTOFlexReports/DriverSummaryReport/DriverSummaryReport';
import OTOFlexReports from 'pages/Reports/OTOFlexReports/OtoFlexReports';
import TransactionReport from 'pages/Reports/OTOFlexReports/TransactionReport/TransactionReport';
import BinTransactions from 'pages/Reports/OTOPackReports/BinTransactions/BinTransactions';
import DailyStaff from 'pages/Reports/OTOPackReports/DailyStaff/DailyStaff';
import InventoryTransactions from 'pages/Reports/OTOPackReports/InventoryTransactions/InventoryTransactions';
import OrderBasedTransaction from 'pages/Reports/OTOPackReports/OrderBasedTransaction/OrderBasedTransaction';
import OtoPackReports from 'pages/Reports/OTOPackReports/OtoPackReports';
import PackingStatusReports from 'pages/Reports/OTOPackReports/PackingStatusReports/PackingStatusReports';
import PickingExceptionsLogs from 'pages/Reports/OTOPackReports/PickingExceptionsLogs/PickingExceptionsLogs';
import ReplenishmentReport from 'pages/Reports/OTOPackReports/ReplenishmentReport/ReplenishmentReport';
import StockUpdate from 'pages/Reports/OTOPackReports/StockUpdate/StockUpdate';
import WaveBasedTransaction from 'pages/Reports/OTOPackReports/WaveBasedTransaction/WaveBasedTransaction';
import AgedOrdersReport from 'pages/Reports/OrderReports/AgedOrdersReport/AgedOrdersReport';
import CustomReport1 from 'pages/Reports/OrderReports/CustomReport1/CustomReport1';
import CustomerFeedbacks from 'pages/Reports/OrderReports/CustomerFeedbacks/CustomerFeedbacks';
import DailyOrderStats from 'pages/Reports/OrderReports/DailyOrderStats/DailyOrderStats';
import OrderHeatmap from 'pages/Reports/OrderReports/OrderHeatmap/OrderHeatmap';
import OrderReports from 'pages/Reports/OrderReports/OrderReports';
import OrderStatusLogs from 'pages/Reports/OrderReports/OrderStatusLogs/OrderStatusLogs';
import OrdersByProductReport from 'pages/Reports/OrderReports/OrdersByProductReport/OrdersByProductReport';
import ResentOrders from 'pages/Reports/OrderReports/ResentOrders/ResentOrders';
import SLABreachesReport from 'pages/Reports/OrderReports/SLABreachesReport/SLABreachesReport';
import SalesBySKU from 'pages/Reports/OrderReports/SalesBySKU/SalesBySKU';
import TimeStatsReport from 'pages/Reports/OrderReports/TimeStatsReports/TimeStatsReport';
import Reports from 'pages/Reports/Reports/Reports';
import AggregatorProfitReport from 'pages/Reports/ShipmentReports/AggregatorProfitReport/AggregatorProfitReport';
import DeliveryAttemptReport from 'pages/Reports/ShipmentReports/DeliveryAttemptReport/DeliveryAttemptReport';
import DeliveryCompanyReportByCity from 'pages/Reports/ShipmentReports/DeliveryCompanyReportByCity/DeliveryCompanyReportByCity';
import FailedDelivery from 'pages/Reports/ShipmentReports/FailedDelivery/FailedDelivery';
import IntegratorSLAReport from 'pages/Reports/ShipmentReports/IntegratorSLAReport/SLAReport';
import PaidCODReports from 'pages/Reports/ShipmentReports/PaidCODReports/PaidCODReport';
import PickupDeliveryReport from 'pages/Reports/ShipmentReports/PickupDeliveryReport/PickupDeliveryReport';
import ShipmentReports from 'pages/Reports/ShipmentReports/ShipmentReports';
import ShipmentStatusReport from 'pages/Reports/ShipmentReports/ShipmentStatusReport/ShipmentStatusReport';
import RoleTemplates from 'pages/RoleTemplates/RoleTemplates';
import ConnectedStores from 'pages/SalesChannels/ConnectedStores/ConnectedStores';
import OnlineStores from 'pages/SalesChannels/OnlineStores/OnlineStores';
import SalesChannelDetail from 'pages/SalesChannels/SalesChannelDetail';
import SalesChannels from 'pages/SalesChannels/SalesChannels';
import SavingsCalculator from 'pages/SavingsCalculator/SavingsCalculator';
import SenderLocations from 'pages/SenderLocations/SenderLocations';
import Area from 'pages/Settings/Area/Area';
import AreaSettingList from 'pages/Settings/Area/AreaSettings/AreaSettingList';
import CityGroupList from 'pages/Settings/Area/CityGroup/CityGroupList';
import Autocorrector from 'pages/Settings/Autocorrector/Autocorrector';
import BlacklistedCustomerList from 'pages/Settings/BlacklistedCustomers/BlacklistedCustomerList';
import BoxList from 'pages/Settings/Box/BoxList';
import BrandList from 'pages/Settings/Brands/BrandList';
import ContentPreferences from 'pages/Settings/ContentPreferences/ContentPreferences';
import CustomizablePagesList from 'pages/Settings/CustomizablePages/CustomizablePagesList';
import Email from 'pages/Settings/Email/Email';
import EmailAccountList from 'pages/Settings/Email/EmailAccounts/EmailAccountList';
import EmailSettingList from 'pages/Settings/Email/EmailSettings/EmailSettingList';
import GeneralSettings from 'pages/Settings/General/GeneralSettings';
import ExportLogList from 'pages/Settings/ImportExport/ExportLog/ExportLogList';
import ImportLogList from 'pages/Settings/ImportExport/ImportLog/ImportLogList';
import Invoice from 'pages/Settings/Invoices/Invoice/InvoiceList';
import InvoiceDetail from 'pages/Settings/Invoices/InvoiceDetails/InvoiceDetailList';
import Invoices from 'pages/Settings/Invoices/Invoices/Invoices';
import NewRecord from 'pages/Settings/Invoices/NewRecords/NewRecordList';
import NotInvoicedOrders from 'pages/Settings/Invoices/NotInvoicedOrders/NotInvoicedOrderList';
import PriceNotFoundOrders from 'pages/Settings/Invoices/PriceNotFoundOrders/PriceNotFoundOrderList';
import Localization from 'pages/Settings/Localization/Localization';
import OTOFlexSettings from 'pages/Settings/OTOFlex/OTOFlexSettings';
import OTOPack from 'pages/Settings/OTOPack/OTOPack';
import CODWallet from 'pages/Settings/Payment/CODWallet/CODWallet';
import ManageSubscription from 'pages/Settings/Payment/ManageSubscription/ManageSubscription';
import ReferralList from 'pages/Settings/Payment/ReferralList/ReferralList';
import Referrals from 'pages/Settings/Payment/Referrals/Referrals';
import ContractedShipmentsList from 'pages/Settings/Payment/Transactions/ContractedShipments/ContractedShipmentsList';
import CreditTransactionList from 'pages/Settings/Payment/Transactions/CreditTransactions/CreditTransactionList';
import FreeCreditTransactionList from 'pages/Settings/Payment/Transactions/FreeCreditTransactions/FreeCreditTransactionList';
import OTOLinkTransactions from 'pages/Settings/Payment/Transactions/OTOLinkTransactions/OTOLinkTransactions';
import SubscriptionTransactionList from 'pages/Settings/Payment/Transactions/SubscriptionTransactions/SubscriptionTransactionList';
import Payments from 'pages/Settings/Payment/Transactions/Transactions';
import Print from 'pages/Settings/Print/Print';
import NotificationSettings from 'pages/Settings/Profile/NotificationSettings';
import SecuritySettings from 'pages/Settings/Profile/SecuritySettings';
import UserSettings from 'pages/Settings/Profile/UserSettings';
import PredefinedMessagesList from 'pages/Settings/ReasonConfig/PredefinedMessagesList/PredefinedMessagesList';
import Reason from 'pages/Settings/ReasonConfig/ReasonConfig';
import ReasonConfig from 'pages/Settings/ReasonConfig/ReasonConfigList/ReasonConfigList';
import ConnectedProviders from 'pages/Settings/SMS/SMSProviders/ConnectedProviders';
import Providers from 'pages/Settings/SMS/SMSProviders/Providers';
import SMSProviders from 'pages/Settings/SMS/SMSProviders/SMSProviders';
import SMSSettingList from 'pages/Settings/SMS/SMSSettingList';
import InvoiceCheckerHistoryList from 'pages/Settings/ShippingPrices/InvoiceCheckerHistory/InvoiceCheckerHistoryList';
import ShippingPrice from 'pages/Settings/ShippingPrices/ShippingPrice';
import ShippingPriceTransactionsList from 'pages/Settings/ShippingPrices/ShippingPriceTransactions/ShippingPriceTransactionsList';
import ShippingPriceList from 'pages/Settings/ShippingPrices/ShippingPrices/ShippingPriceList';
import WhatsappBusiness from 'pages/Settings/Whatsapp/WhatsappBusiness';
import WhatsappSettingList from 'pages/Settings/Whatsapp/WhatsappSettings/WhatsappSettingList';
import WhatsappTemplateList from 'pages/Settings/Whatsapp/WhatsappTemplates/WhatsappTemplateList';
import ShipmentTracking from 'pages/ShipmentTracking/ShipmentTracking';
import AllOrderList from 'pages/Shipments/AllOrder/AllOrderList';
import AwaitingPickupList from 'pages/Shipments/AwaitingPickup/AwaitingPickupList';
import CanceledOrderList from 'pages/Shipments/Canceled/CanceledOrderList';
import CurrentlyShippingList from 'pages/Shipments/CurrentlyShipping/CurrentlyShippingList';
import DeliveredOrderList from 'pages/Shipments/Delivered/DeliveredOrderList';
import FoodicsShipmentsList from 'pages/Shipments/FoodicsShipments/FoodicsShipmentsList';
import OnHoldOrderList from 'pages/Shipments/OnHold/OnHoldOrderList';
import AggregatorAging from 'pages/Shipments/Operation/AggregatorAging/AggregatorAging';
import AggregatorAgingOverview from 'pages/Shipments/Operation/AggregatorAging/AggregatorAgingOverview';
import AggregatorAgingShipments from 'pages/Shipments/Operation/AggregatorAging/AggregatorAgingShipments';
import AggregatorAgingSummary from 'pages/Shipments/Operation/AggregatorAging/AggregatorAgingSummary';
import AggregatorDCPerformance from 'pages/Shipments/Operation/AggregatorDCPerformance/AggregatorDCPerformance';
import OperationAllOrder from 'pages/Shipments/Operation/AllOrders/AllOrders';
import NeedActionShipmentsList from 'pages/Shipments/Operation/AllOrders/NeedActionShipmentsList';
import OperationAllOrderList from 'pages/Shipments/Operation/AllOrders/OperationAllOrderList';
import BulletTracking from 'pages/Shipments/Operation/BulletTrackings/BulletTracking';
import BulletTrackingList from 'pages/Shipments/Operation/BulletTrackings/BulletTrackingList';
import OverView from 'pages/Shipments/Operation/BulletTrackings/Overview';
import CreateShipmentErrorMapping from 'pages/Shipments/Operation/CreateShipmentErrorMapping/CreateShipmentErrorMapping';
import CreateShipmentErrorMappingList from 'pages/Shipments/Operation/CreateShipmentErrorMapping/CreateShipmentErrorMappingList';
import ShipmentErrorLogsByAiList from 'pages/Shipments/Operation/CreateShipmentErrorMapping/ShipmentErrorLogsByAiList';
import DeliveryAttemptReportList from 'pages/Shipments/Operation/DeliveryAttemptReport/DeliveryAttemptReportList';
import PickupDeliveryReportOperations from 'pages/Shipments/Operation/PickupDeliveryReportOperation/PickupDeliveryReportOperation';
import PickupRequestList from 'pages/Shipments/Operation/PickupRequests/PickupRequestList';
import PromoCodeList from 'pages/Shipments/Operation/Promotions/PromoCodeList';
import PromoTransactionsList from 'pages/Shipments/Operation/Promotions/PromoTransactionsList';
import Promotions from 'pages/Shipments/Operation/Promotions/Promotions';
import TimeStatsForOperationList from 'pages/Shipments/Operation/TimeStatsForOperation/TimeStatsForOperationList';
import ZeroDC from 'pages/Shipments/Operation/ZeroDCResults/ZeroDC';
import ZeroDCDetailed from 'pages/Shipments/Operation/ZeroDCResults/ZeroDCDetailed';
import ZeroDCGrouped from 'pages/Shipments/Operation/ZeroDCResults/ZeroDCGrouped';
import PendingOrderList from 'pages/Shipments/PendingOrders/PendingOrderList';
import OTOAWB from 'pages/Shipments/PrintOrders/OTOAWB';
import ReturnedOrderList from 'pages/Shipments/Returned/ReturnedOrderList';
import Shipments from 'pages/Shipments/Shipments/Shipments';
import DeliveryCompanyFeasibilitiesList from 'pages/ShippingOtomation/DeliveryCompanyFeasibilities/DeliveryCompanyFeasibilitiesList';
import FeasibilityRuleList from 'pages/ShippingOtomation/FeasibilityRules/FeasibilityRuleList';
import OtomationRulesList from 'pages/ShippingOtomation/OtomationRules/OtomationRulesList';
import PriorityRuleList from 'pages/ShippingOtomation/PriorityRules/PriorityRuleList';
import ShippingOtomation from 'pages/ShippingOtomation/ShippingOtomation';
import CityCoverageList from 'pages/ShippingPartners/ConnectedPartners/CityCoverageList';
import Connected from 'pages/ShippingPartners/ConnectedPartners/ConnectedPartners';
import DeliverySlotManagement from 'pages/ShippingPartners/ConnectedPartners/DeliverySlotManagement';
import SLAConfigurationForm from 'pages/ShippingPartners/ConnectedPartners/SLAConfigurationForm';
import MoreConnection from 'pages/ShippingPartners/MoreConnection/MoreConnection';
import ShippingPartnerForm from 'pages/ShippingPartners/ShippingPartnerForm/ShippingPartnerForm';
import ShippingPartners from 'pages/ShippingPartners/ShippingPartners';
import AddressManagement from 'pages/SupportAdmin/AddressManagement/AddressManagement';
import CityList from 'pages/SupportAdmin/AddressManagement/City/CityList';
import CountryList from 'pages/SupportAdmin/AddressManagement/Country/CountryList';
import DistrictList from 'pages/SupportAdmin/AddressManagement/District/DistrictList';
import GovernorateList from 'pages/SupportAdmin/AddressManagement/Governorate/GovernorateList';
import NeighbourhoodList from 'pages/SupportAdmin/AddressManagement/Neighbourhood/NeighbourhoodList';
import StateList from 'pages/SupportAdmin/AddressManagement/State/StateList';
import UsersSupportList from 'pages/SupportAdmin/ClientUsers/UsersSupportList';
import DCPickupLocationList from 'pages/SupportAdmin/DCPickupLocationList/DCPickupLocationList';
import DCStatusManagement from 'pages/SupportAdmin/DCStatusManagement/DCStatusManagement';
import MissingMappngStatuses from 'pages/SupportAdmin/DCStatusManagement/MissingMappingStatuses/MissingMapingStatuses';
import MissingMappingStatusesDetailedList from 'pages/SupportAdmin/DCStatusManagement/MissingMappingStatuses/MissingMappingStatusesDetailedList';
import MissingMappingStatusesGroupedList from 'pages/SupportAdmin/DCStatusManagement/MissingMappingStatuses/MissingMappingStatusesGroupedList';
import StatusGroupTransitions from 'pages/SupportAdmin/DCStatusManagement/StatusGroupTransitions/StatusGroupTransitionsList';
import StatusGroupsList from 'pages/SupportAdmin/DCStatusManagement/StatusGroups/StatusGroupsList';
import DCStatusMappingList from 'pages/SupportAdmin/DCStatusManagement/StatusMapping/DCStatusMappingList';
import OtoOrderStatusesList from 'pages/SupportAdmin/DCStatusManagement/Statuses/StatusesList';
import DeliveryCompanies from 'pages/SupportAdmin/DeliveryCompanies/DeliveryCompanies';
import TermsAndConditions from 'pages/TermsAndConditions/TermsAndConditions';
import Notifications from 'pages/Topbar/Notifications/Notifications';
import UserList from 'pages/Users/UserList';
import BinsList from 'pages/WMSModule/Configuration/Bins/BinsList';
import Configuration from 'pages/WMSModule/Configuration/Configuration';
import UserGroupList from 'pages/WMSModule/Configuration/UserGroups/UserGroupList';
import WmsUserList from 'pages/WMSModule/Configuration/Users/WmsUserList';
import VendorsList from 'pages/WMSModule/Configuration/Vendors/VendorList';
import ZonesList from 'pages/WMSModule/Configuration/Zones/ZonesList';
import BinInventoryList from 'pages/WMSModule/Inventory/BinInventory/BinInventoryList';
import GlobalInventoryList from 'pages/WMSModule/Inventory/GlobalInventory/GlobalInventoryList';
import Inventory from 'pages/WMSModule/Inventory/Inventory';
import InventoryList from 'pages/WMSModule/Inventory/Inventory/InventoryList';
import InboundOrders from 'pages/WMSModule/Operations/Inbound/InboundOrdersList';
import Operations from 'pages/WMSModule/Operations/Operations';
import OutboundOrders from 'pages/WMSModule/Operations/Outbound/OutboundOrdersList';
import PackingList from 'pages/WMSModule/Operations/Packing/PackingList';
import PutawayList from 'pages/WMSModule/Operations/Putaway/PutawayList';
import ReturnedList from 'pages/WMSModule/Operations/Returned/ReturnedList';
import SortingList from 'pages/WMSModule/Operations/Sorting/SortingList';
import WaveList from 'pages/WMSModule/Operations/Waves/WaveList';
import PurchaseOrders from 'pages/WMSModule/PurchaseOrders/PurchaseOrders';
import WHDashboard from 'pages/WarehouseUser/WHDashboard/WHDashboard';
import WHHistoryList from 'pages/WarehouseUser/WHHistory/WHHistoryList';
import WHPendingOrderList from 'pages/WarehouseUser/WHPendingOrders/WHPendingOrderList';
import WHShipmentInProgressList from 'pages/WarehouseUser/WHShipmentInProgress/WHShipmentInProgressList';
import WHShipmentOnHoldList from 'pages/WarehouseUser/WHShipmentOnHold/WHShipmentOnHoldList';
import WHWaitingPickupList from 'pages/WarehouseUser/WHWaitingPickup/WHWaitingPickupList';
import WarehouseList from 'pages/Warehouses/WarehouseList';
import Webhooks from 'pages/Webhooks/Webhooks';

import PATHS, { mergePaths } from 'utils/paths';

import MainPage from './MainPage';
import ProtectedRoutes from './ProtectedRoutes';
import PublicRoutes from './PublicRoutes';

const Router = () => {
	const { t } = useTranslation();
	const { pathname } = useLocation();

	/**
	 * @description Text Transform function
	 */

	const getTitleFromPath = () => {
		let foundTitle = '';
		const path = pathname.split('/')[1];

		sideBarConfig.forEach((category) => {
			category.links.forEach((link) => {
				if (link.path === `/${path}`) {
					foundTitle = t(link.title);
				}
			});
		});

		if (foundTitle === '') {
			settingsSidebarConfig.forEach((category) => {
				category.links.forEach((link) => {
					if (link.path === `/${path}`) {
						foundTitle = t(link.title);
					}
				});
			});
		}

		return foundTitle;
	};

	useEffect(() => {
		const path = pathname.split('/')[1];
		if (path === 'login' || path === 'register') return;

		const newTitle = getTitleFromPath();

		document.title = newTitle ? `OTO - ${t(newTitle)}` : `OTO`;
	}, [pathname, t]);
	return (
		<Suspense fallback={<Loading />}>
			<Routes>
				<Route path={PATHS.EXTERNAL_PRINT} element={<ExternalPrint />} />
				<Route path={PATHS.SMS} element={<SmsLayout />} />
				<Route path={PATHS.SMS_ERROR} element={<NotFoundSms />} />
				<Route path={PATHS.SMS_EXPIRED} element={<SmsLinkExpired />} />
				<Route path={PATHS.PDF_ERROR} element={<NotFoundSms />} />
				<Route path={PATHS.INTEGRATION_FRAME} element={<AppIntegrationFrame />} />
				<Route path={PATHS.SHIPMENT_TRACKING} element={<ShipmentTracking />} />
				<Route path={PATHS.PRICE_CALCULATOR_WEB} element={<PriceCalculatorWeb />} />
				<Route path={PATHS.OTOFLEX_TRACKING} element={<OTOFlexTracking />} />
				<Route path={PATHS.OTO_LINK_BUYER} element={<OTOLinkBuyer />} />

				{/* Don't change protected and public routes order! */}
				<Route element={<ProtectedRoutes />}>
					{/* USER */}
					<Route path={PATHS.HOME} element={<Home />} />
					<Route
						path={PATHS.SHIPMENTS_PRICE_CALCULATOR}
						element={<PriceCalculator key={PATHS.SHIPMENTS_PRICE_CALCULATOR} />}
					/>
					<Route path={PATHS.SHIPMENTS} element={<Shipments />}>
						<Route path={PATHS.PENDING_ORDERS} element={<PendingOrderList />} />
						<Route path={PATHS.AWAITING_PICKUP} element={<AwaitingPickupList />} />
						<Route path={PATHS.CURRENTLY_SHIPPING} element={<CurrentlyShippingList />} />
						<Route path={PATHS.ONHOLD_ORDERS} element={<OnHoldOrderList />} />
						<Route path={PATHS.DELIVERED_ORDERS} element={<DeliveredOrderList />} />
						<Route path={PATHS.RETURNED_ORDERS} element={<ReturnedOrderList />} />
						<Route path={PATHS.CANCELED_ORDERS} element={<CanceledOrderList />} />
						<Route path={PATHS.ALL_ORDERS} element={<AllOrderList />} />
					</Route>
					<Route path={PATHS.FOODICS_SHIPMENTS} element={<FoodicsShipmentsList />} />
					<Route path={PATHS.IMPORT_ORDERS} element={<ImportOrders />} />
					<Route path={PATHS.IMPORT_CALCULATOR} element={<PriceCalculator type="import" />} />
					<Route
						path={PATHS.IMPORT_ORDERS_GET_ESTIMATE}
						element={<PriceCalculator key={PATHS.IMPORT_ORDERS_GET_ESTIMATE} type="import" />}
					/>
					<Route
						path={PATHS.CREATE_SHIPMENT}
						element={<PriceCalculator key={PATHS.CREATE_SHIPMENT} type="quickShipment" />}
					/>

					<Route path={PATHS.BR_PENDING_ORDERS} element={<BRPendingOrderList />} />
					<Route path={PATHS.BR_WAITING_FOR_DRIVER} element={<BRWaitingForDriverList />} />
					<Route path={PATHS.BR_RETURNED} element={<BRReturnedOrderList />} />
					<Route path={PATHS.BR_SHIPMENT_IN_PROGRESS} element={<BRShipmentInProgressList />} />
					<Route path={PATHS.BR_DASHBOARD} element={<BRDashboard />} />
					<Route path={PATHS.BR_DELIVERED} element={<BRDeliveredOrderList />} />
					<Route path={PATHS.BR_CANCELED} element={<BRCanceledOrderList />} />
					<Route path={PATHS.BR_UNASSINGED} element={<BRUnassignedOrderList />} />
					<Route path={PATHS.DR_HOME} element={<DriverHome />} />
					<Route path={PATHS.WH_DASHBOARD} element={<WHDashboard />} />
					<Route path={PATHS.WH_PENDING_ORDERS} element={<WHPendingOrderList />} />
					<Route path={PATHS.WH_WAITING_PICKUP} element={<WHWaitingPickupList />} />
					<Route path={PATHS.WH_SHIPMENT_ONHOLD} element={<WHShipmentOnHoldList />} />
					<Route path={PATHS.WH_SHIPMENT_INPROGRESS} element={<WHShipmentInProgressList />} />
					<Route path={PATHS.WH_HISTORY} element={<WHHistoryList />} />
					<Route path={PATHS.OTO_AI_CLIENT_CONFIGURATION} element={<OTOAIClientConfigurations />} />
					<Route path={PATHS.USERS} element={<UserList />} />
					<Route path={PATHS.BRANCHES} element={<BranchList />} />
					<Route path={PATHS.WAREHOUSES} element={<WarehouseList />} />
					<Route path={PATHS.BRANDS} element={<BrandList />} />
					<Route path={PATHS.API_INTEGRATIONS} element={<OtoAPI />}>
						<Route path={PATHS.OTO_API} element={<APIIntegrations />} />
						<Route path={PATHS.CONNECTED_TOKENS} element={<ConnectedTokens />} />
					</Route>
					<Route path={PATHS.WEBHOOKS} element={<Webhooks />} />
					<Route path={PATHS.SALES_CHANNELS} element={<SalesChannels />}>
						<Route path={PATHS.ONLINE_STORES} element={<OnlineStores />} />
						<Route path={PATHS.CONNECTED_STORES} element={<ConnectedStores />} />
					</Route>
					<Route path={PATHS.SALES_CHANNELS_DETAILS} element={<SalesChannelDetail />} />

					<Route path={PATHS.PRODUCTS_MAIN} element={<Product />}>
						<Route path={PATHS.PRODUCTS} element={<ProductList />} />
						<Route path={PATHS.PRODUCTS_CATEGORIES} element={<CategoriesList />} />
						<Route path={PATHS.PRODUCT_ATTRIBUTES} element={<ProductAttributesList />} />
						<Route path={PATHS.ATTRIBUTES_GROUPS} element={<AttributesGroupsList />} />
					</Route>

					<Route path={PATHS.OTO_FLEX_MAIN} element={<OTOFlex />}>
						<Route path={PATHS.UNASSIGNED_ORDERS} element={<UnassignedOrderList />} />
						<Route path={PATHS.HISTORY_ORDERS_SUB} element={<HistoryOrderList />} />
						<Route path={PATHS.PROCESSING_ORDERS_SUB} element={<ProcessingOrderList />} />
					</Route>
					<Route path={PATHS.DRIVERS} element={<DriverList />} />
					<Route path={PATHS.OTO_LINK} element={<OTOLink />}>
						<Route path={PATHS.ALL_LINKS} element={<AllLinks />} />
						<Route path={PATHS.ACTIVE_LINKS} element={<ActiveLinks />} />
						<Route path={PATHS.ORDERS} element={<OTOLinkOrders />} />
						<Route path={PATHS.OTO_LINK_TRANSACTIONS} element={<OTOLinkTransactions />} />
					</Route>

					<Route path={PATHS.OTO_STORE} element={<OTOStore />} />
					<Route path={PATHS.OTO_STORE_MY_ORDERS} element={<OTOStoreMyOrders />} />
					<Route path={PATHS.OTO_STORE_PRODUCT} element={<OTOStoreProductDetails />} />
					<Route path={PATHS.OTO_STORE_CHECKOUT} element={<OTOStoreCheckout />} />

					<Route path={PATHS.SHIPPING_AUTOMATION} element={<ShippingOtomation />}>
						<Route path={PATHS.FEASIBILITY_RULES} element={<FeasibilityRuleList />} />
						<Route path={PATHS.PRIORITY_RULES} element={<PriorityRuleList />} />
						<Route path={PATHS.OTOMATION_RULES} element={<OtomationRulesList />} />
						<Route
							path={PATHS.DELIVERY_COMPANY_FEASIBILITY}
							element={<DeliveryCompanyFeasibilitiesList />}
						/>
					</Route>
					<Route path={PATHS.ANALYSIS} element={<Analysis />}>
						<Route path={PATHS.ANALYSIS_TAB} element={<Overview />} />
						<Route path={PATHS.SLA_PERFORMANCE} element={<SLAPerformance />} />
						<Route path={PATHS.DELIVERY_COMPANY} element={<DeliveryCompanyTab />} />
						<Route path={PATHS.SHIPPING_ACCOUNTING} element={<ShippingAccountingTab />} />
						<Route path={PATHS.SALES} element={<SalesTab />} />
						<Route path={PATHS.CUSTOMER_DETAILS} element={<CustomerDetailsTab />} />
						<Route path={PATHS.OTO_FLEX} element={<OTOFlexTab />} />
						<Route path={PATHS.OTO_PACK} element={<OTOPackTab />} />
					</Route>
					<Route path={PATHS.PRICE_CALCULATOR} element={<PriceCalculator key={PATHS.PRICE_CALCULATOR} />} />
					<Route path={PATHS.CUSTOMER_SERVICE} element={<CustomerService />}>
						<Route path={PATHS.RETURNS} element={<ReturnsList />} />
						<Route path={PATHS.FAILED_DELIVERY_ORDERS} element={<FailedDeliveryOrdersList />} />
						<Route path={PATHS.CUSTOMER_SERVICE_FEEDBACKS} element={<CustomerServiceFeedbacksList />} />
					</Route>
					<Route path={PATHS.HISTORY_ORDERS} element={<HistoryOrdersList />} />

					<Route path={PATHS.PROCESSING_ORDERS} element={<ProcessingOrdersList />} />
					<Route path={PATHS.DASHBOARD} element={<Dashboard />} />
					<Route path={PATHS.DASHBOARD_MONITOR_VIEW} element={<DashboardMonitor />} />
					<Route path={PATHS.UNIT_ECONOMICS_DASHBOARD} element={<UnitEconomicsDashboard />} />
					<Route path={PATHS.COMPANY_KPI_DASHBOARD} element={<CompanyKPIDashboard />} />
					<Route path={PATHS.GENERAL_SETTINGS} element={<GeneralSettings />} />
					<Route path={PATHS.LOCALIZATION_SETTINGS} element={<Localization />} />
					<Route path={PATHS.COD_WALLET} element={<CODWallet />} />
					<Route path={PATHS.PAID_COD} element={<PaidCODReports />} />
					<Route path={PATHS.PAYMENTS} element={<Payments />}>
						<Route path={PATHS.CREDIT_TRANSACTIONS} element={<CreditTransactionList />} />
						<Route path={PATHS.SUBSCRIPTION_TRANSACTIONS} element={<SubscriptionTransactionList />} />
						<Route path={PATHS.FREE_CREDIT_TRANSACTIONS} element={<FreeCreditTransactionList />} />
						<Route path={PATHS.OTO_LINK_TRANSACTIONS} element={<OTOLinkTransactions />} />
						<Route path={PATHS.CONTRACTED_SHIPMENTS} element={<ContractedShipmentsList />} />
					</Route>

					<Route path={PATHS.MANAGE_SUBSCRIPTION} element={<ManageSubscription />} />
					<Route path={PATHS.PAYMENT_NAMAGEMENT} element={<ManageSubscription />} />
					<Route path={PATHS.ORDER_AUTOMATION} element={<OrderOtomation />} />
					<Route path={PATHS.PRINT_SETTINGS} element={<Print />} />
					<Route path={PATHS.SMS_SETTINGS} element={<SMSSettingList />} />
					<Route path={PATHS.SMS_PROVIDERS} element={<SMSProviders />}>
						<Route path={PATHS.ALL_PROVIDERS} element={<Providers />} />
						<Route path={PATHS.CONNECTED_PROVIDERS} element={<ConnectedProviders />} />
					</Route>
					<Route path={PATHS.OTOFLEX_SETTINGS} element={<OTOFlexSettings />} />
					<Route path={PATHS.OTOPACK_SETTINGS} element={<OTOPack />} />
					<Route path={PATHS.PICKUP_LOCATION} element={<SenderLocations />} />
					<Route path={PATHS.CONTENT_PREFERENCES} element={<ContentPreferences />} />
					<Route path={PATHS.CUSTOMIZABLE_PAGES} element={<CustomizablePagesList />} />

					<Route path={PATHS.IMPORT_LOGS} element={<ImportLogList />} />
					<Route path={PATHS.EXPORT_LOGS} element={<ExportLogList />} />

					<Route path={PATHS.BOX_SETTINGS} element={<BoxList />} />
					<Route path={PATHS.REASON} element={<Reason />}>
						<Route path={PATHS.REASON_CONFIG} element={<ReasonConfig />} />
						<Route path={PATHS.PREDEFINED_MESSAGES} element={<PredefinedMessagesList />} />
					</Route>
					<Route path={PATHS.REASON_CONFIG} element={<ReasonConfig />} />
					<Route path={PATHS.BLACKLISTED_CUSTOMERS} element={<BlacklistedCustomerList />} />
					<Route path={PATHS.USER_SETTINGS} element={<UserSettings />} />
					<Route path={PATHS.SECURITY_SETTINGS} element={<SecuritySettings />} />
					<Route path={PATHS.NOTIFICATION_SETTINGS} element={<NotificationSettings />} />
					<Route path={PATHS.AUTOCORRECTOR} element={<Autocorrector />} />
					<Route path={PATHS.REFERRALS} element={<Referrals />} />
					<Route path={PATHS.REFERRAL_LIST} element={<ReferralList />} />
					<Route path={PATHS.EMAIL} element={<Email />}>
						<Route path={PATHS.EMAIL_ACCOUNTS} element={<EmailAccountList />} />
						<Route path={PATHS.EMAIL_SETTINGS} element={<EmailSettingList />} />
					</Route>
					<Route path={PATHS.AREA} element={<Area />}>
						<Route path={PATHS.AREA_SETTINGS} element={<AreaSettingList />} />
						<Route path={PATHS.CITY_GROUP} element={<CityGroupList />} />
					</Route>
					<Route path={PATHS.SHIPPING_PRICE} element={<ShippingPrice />}>
						<Route path={PATHS.SHIPPING_PRICES} element={<ShippingPriceList />} />
						<Route path={PATHS.SHIPPING_PRICE_TRANSACTIONS} element={<ShippingPriceTransactionsList />} />
						<Route path={PATHS.INVOICE_CHECKER_HISTORY} element={<InvoiceCheckerHistoryList />} />
					</Route>
					<Route path={PATHS.SHIPPING_PARTNERS} element={<ShippingPartners />}>
						<Route path={PATHS.MORE_CONNECTION} element={<MoreConnection />} />
						<Route path={PATHS.CONNECTED_PARTNERS} element={<Connected />} />
					</Route>
					<Route
						path={mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.CITY_COVERAGE)}
						element={<CityCoverageList />}
					/>
					<Route path={PATHS.SHIPPING_PARTNER_FORM} element={<ShippingPartnerForm />} />
					<Route path={PATHS.SHIPPING_PARTNER_SLA_FORM} element={<SLAConfigurationForm />} />

					<Route
						path={PATHS.SHIPPING_PARTNER_DELIVERY_SLOT_CONFIGURATION_FORM}
						element={<DeliverySlotManagement />}
					/>

					<Route path={PATHS.OTHER_INTEGRATIONS} element={<Navigation />}>
						<Route path={PATHS.INTEGRATIONS} element={<Integrations />} />
						<Route path={PATHS.CONNECTED_INTEGRATIONS} element={<ConnectedIntegrations />} />
					</Route>
					<Route path={PATHS.REPORTS} element={<Reports />}>
						<Route path={PATHS.ORDER_REPORTS} element={<OrderReports />} />
						<Route path={PATHS.SHIPMENT_REPORTS} element={<ShipmentReports />} />
						<Route path={PATHS.OTO_PACK_REPORTS} element={<OtoPackReports />} />
						<Route path={PATHS.OTOFLEX_REPORTS} element={<OTOFlexReports />} />
						<Route path={PATHS.BRANCH_REPORTS} element={<BranchReports />} />
					</Route>
					<Route path={PATHS.FOODICS_SHIPPING_TRANSACTION} element={<FoodicsShippingTransactionReport />} />
					<Route path={PATHS.BIN_TRANSACTIONS} element={<BinTransactions />} />
					<Route path={PATHS.DAILY_STAFF} element={<DailyStaff />} />
					<Route path={PATHS.STOCK_UPDATE_REPORTS} element={<StockUpdate />} />
					<Route path={PATHS.ORDER_BASED_TRANSACTION} element={<OrderBasedTransaction />} />
					<Route path={PATHS.WAVE_BASED_TRANSACTION} element={<WaveBasedTransaction />} />
					<Route path={PATHS.INVENTORY_TRANSACTIONS} element={<InventoryTransactions />} />
					<Route path={PATHS.REPLENISHMENT_REPORT} element={<ReplenishmentReport />} />
					<Route path={PATHS.PICKING_EXCEPTION_LOGS} element={<PickingExceptionsLogs />} />
					<Route path={PATHS.PACKING_STATUS_REPORTS} element={<PackingStatusReports />} />
					<Route path={PATHS.DRIVER_SUMMARY} element={<DriverSummary />} />
					<Route path={PATHS.DRIVER_DETAILS} element={<DriverDetails />} />
					<Route path={PATHS.ORDER_STATUS_LOGS} element={<OrderStatusLogs />} />
					<Route path={PATHS.DAILY_ORDER_STATS} element={<DailyOrderStats />} />
					<Route path={PATHS.CUSTOMER_FEEDBACKS} element={<CustomerFeedbacks />} />
					<Route path={PATHS.TIME_STATS} element={<TimeStatsReport />} />
					<Route path={PATHS.SLA_BREACHES_REPORT} element={<SLABreachesReport />} />
					<Route path={PATHS.SALES_BY_SKU} element={<SalesBySKU />} />
					<Route path={PATHS.ORDERS_BY_PRODUCT_REPORT} element={<OrdersByProductReport />} />
					<Route path={PATHS.AGED_ORDERS_REPORT} element={<AgedOrdersReport />} />
					<Route path={PATHS.RESENT_ORDERS} element={<ResentOrders />} />
					<Route path={PATHS.ORDER_HEATMAP} element={<OrderHeatmap />} />
					<Route path={PATHS.CUSTOM_REPORT_1} element={<CustomReport1 />} />
					<Route path={PATHS.DELIVERY_COMPANY_REPORT_BY_CITY} element={<DeliveryCompanyReportByCity />} />
					<Route path={PATHS.SHIPMENT_STATUS_REPORT} element={<ShipmentStatusReport />} />
					<Route path={PATHS.FAILED_DELIVERY} element={<FailedDelivery />} />
					<Route path={PATHS.PICKUP_DELIVERY_REPORT} element={<PickupDeliveryReport />} />
					<Route path={PATHS.DELIVERY_ATTEMPT_REPORT} element={<DeliveryAttemptReport />} />
					<Route path={PATHS.INTEGRATOR_SLA_REPORT} element={<IntegratorSLAReport />} />
					<Route path={PATHS.AGGREGATOR_PROFIT_REPORT} element={<AggregatorProfitReport />} />
					<Route path={PATHS.TRANSACTION_REPORT} element={<TransactionReport />} />
					<Route path={PATHS.BRANCH_REPORT} element={<BranchReport />} />
					<Route path={PATHS.BRANCH_DAILY_STAFF_REPORT} element={<BranchDailyStaffReport />} />

					<Route path={PATHS.NOTIFICATIONS} element={<Notifications />} />
					<Route path={PATHS.OTO_MODULES} element={<OTOModules />} />
					<Route path={PATHS.OTO_MODULES_DETAIL} element={<ModuleDetail />} />
					<Route path={PATHS.SYSTEM_LOGS} element={<SystemLogs />}>
						<Route path={PATHS.SHIPMENT_ERROR_LOGS} element={<ShipmentErrorLogList />} />
						<Route path={PATHS.WEBHOOK_ERROR_LOGS} element={<WebhookErrorLogList />} />
						<Route path={PATHS.INVENTORY_SYNC_LOGS} element={<InventorySyncLogList />} />
						<Route path={PATHS.PICKUP_REQUEST_LOGS} element={<PickUpRequestLogList />} />
						<Route path={PATHS.PRODUCT_SYNC_LOGS} element={<ProductSyncLogList />} />
					</Route>
					<Route path={PATHS.INTEGRATION_LOGS} element={<IntegrationLogs />}>
						<Route path={PATHS.UPDATE_SUCCESS_LOGS} element={<UpdateSuccessLogList />} />
						<Route path={PATHS.UPDATE_ERROR_LOGS} element={<UpdateErrorLogList />} />
						<Route path={PATHS.AUTOMATION_SUCCESS_LOGS} element={<AutomationSuccessLogList />} />
						<Route path={PATHS.AUTOMATION_ERROR_LOGS} element={<AutomationErrorLogList />} />
					</Route>
					<Route path={PATHS.NOTIFICATION_LOGS} element={<NotificationLogs />}>
						<Route path={PATHS.SMS_LOGS} element={<SMSLogList />} />
						<Route path={PATHS.EMAIL_LOGS} element={<EmailLogList />} />
					</Route>
					<Route path={PATHS.INVENTORY_MAIN} element={<Inventory />}>
						<Route path={PATHS.INVENTORY} element={<InventoryList />} />
						<Route path={PATHS.BIN_INVENTORY} element={<BinInventoryList />} />
						<Route path={PATHS.GLOBAL_INVENTORY} element={<GlobalInventoryList />} />
					</Route>
					<Route path={PATHS.WMS_CONFIGURATION} element={<Configuration />}>
						<Route path={PATHS.WMS_CONFIGURATION_USERS} element={<WmsUserList />} />
						<Route path={PATHS.WMS_CONFIGURATION_USER_GROUPS} element={<UserGroupList />} />
						<Route path={PATHS.BINS} element={<BinsList />} />
						<Route path={PATHS.ZONES} element={<ZonesList />} />
						<Route path={PATHS.VENDORS} element={<VendorsList />} />
					</Route>
					<Route path={PATHS.OPERATIONS} element={<Operations />}>
						<Route path={PATHS.INBOUND_ORDERS} element={<InboundOrders />} />
						<Route path={PATHS.OUTBOUND_ORDERS} element={<OutboundOrders />} />
						<Route path={PATHS.PUTAWAY} element={<PutawayList />} />
						<Route path={PATHS.PACKING} element={<PackingList />} />
						<Route path={PATHS.WAVES} element={<WaveList />} />
						<Route path={PATHS.RETURN} element={<ReturnedList />} />
						<Route path={PATHS.SORTING} element={<SortingList />} />
					</Route>
					<Route path={PATHS.WHATSAPP} element={<WhatsappBusiness />}>
						<Route path={PATHS.WHATSAPP_SETTINGS} element={<WhatsappSettingList />} />
						<Route path={PATHS.WHATSAPP_TEMPLATES} element={<WhatsappTemplateList />} />
					</Route>
					<Route path={PATHS.PURCHASE_ORDERS} element={<PurchaseOrders />} />
					<Route path={PATHS.WHATSAPP_SETTINGS} element={<WhatsappSettingList />} />
					<Route path={PATHS.ROLE_TEMPLATES} element={<RoleTemplates />} />
					<Route path={PATHS.AUTOMATION} element={<AutomationList />} />

					{/* OPERATION USER START */}
					<Route path={PATHS.OPERATION_ALL_ORDERS} element={<OperationAllOrderList />} />
					<Route path={PATHS.OPERATION_SHIPMENT} element={<OperationAllOrder />}>
						<Route path={PATHS.OPERATION_ALL_ORDERS} element={<OperationAllOrderList />} />
						<Route path={PATHS.OPERATION_NEED_ACTION_SHIPMENT} element={<NeedActionShipmentsList />} />
					</Route>
					<Route path={PATHS.BULLET_TRACKING} element={<BulletTracking />}>
						<Route path={PATHS.BULLET_TRACKING_LIST} element={<BulletTrackingList />} />
						<Route path={PATHS.ANALYSIS_TAB} element={<OverView />} />
					</Route>
					<Route path={PATHS.TIME_STATS_FOR_OPERATION} element={<TimeStatsForOperationList />} />
					<Route path={PATHS.DELIVERY_ATTEMPT_REPORT_FOR_OPERATION} element={<DeliveryAttemptReportList />} />
					<Route path={PATHS.PICKUP_REQUESTS} element={<PickupRequestList />} />
					<Route path={PATHS.AGGREGATOR_DC_PERFORMANCE} element={<AggregatorDCPerformance />} />
					<Route path={PATHS.AGGREGATOR_AGING} element={<AggregatorAging />}>
						<Route path={PATHS.AGGREGATOR_AGING_SHIPMENTS} element={<AggregatorAgingShipments />} />
						<Route path={PATHS.AGGREGATOR_AGING_OVERVIEW} element={<AggregatorAgingOverview />} />
						<Route path={PATHS.AGGREGATOR_AGING_SUMMARY} element={<AggregatorAgingSummary />} />
					</Route>
					<Route path={PATHS.ZERO_DC} element={<ZeroDC />}>
						<Route path={PATHS.ZERO_DC_GROUPED} element={<ZeroDCGrouped />} />
						<Route path={PATHS.ZERO_DC_DETAILED} element={<ZeroDCDetailed />} />
					</Route>

					<Route path={PATHS.PICKUP_DELIVERY_REPORT_LIST} element={<PickupDeliveryReportOperations />} />

					<Route path={PATHS.CONTRACT_CONFIGURATIONS} element={<ContractConfigurations />}>
						<Route path={PATHS.SHIPPING_CONTRACTS} element={<ShippingContractList />} />
						<Route path={PATHS.OTO_DELIVERY_PRICES} element={<OTODeliveryPricesList />} />
						<Route path={PATHS.SUCCESS_INSTRUCTIONS} element={<SuccessInstructionsList />} />
						<Route path={PATHS.SPECIAL_CONTRACT_TERMS} element={<SpecialContractTermsList />} />
						<Route path={PATHS.OTO_MARGIN} element={<OTOMarginList />} />
					</Route>

					<Route path={PATHS.PRICE_CONFIGURATIONS} element={<PriceConfigurations />}>
						<Route path={PATHS.TIER_BASED_PRICES} element={<TierBasedPriceList />} />
						<Route path={PATHS.WEIGHT_BASED_PRICES} element={<WeightBasedPriceList />} />
						<Route path={PATHS.WEIGHT_COUNTRY_BASED_PRICES} element={<WeightCountryBasedPriceList />} />
						<Route path={PATHS.CITY_BASED_PRICES} element={<CityBasedPricesList />} />
						<Route path={PATHS.KM_BASED_PRICES} element={<KMBasedPriceList />} />
					</Route>

					<Route path={PATHS.SLA_CONFIGURATIONS} element={<SLAConfigurations />}>
						<Route path={PATHS.KM_BASED_SLA} element={<KMBasedSLAList />} />
						<Route path={PATHS.BULLET_KM_SLA} element={<BulletKMSLAList />} />
						<Route path={PATHS.BULLET_DISTRICT_SLA} element={<BulletDistrictSLAList />} />
						<Route path={PATHS.TIER_BASED_SLA} element={<TierBasedSLAList />} />
						<Route path={PATHS.CITY_BASED_SLA} element={<CityBasedSLAList />} />
						<Route path={PATHS.DISTRICT_BASED_SLA} element={<DistrictBasedSLAList />} />
						<Route path={PATHS.COUNTRY_BASED_SLA} element={<CountryBasedSLAList />} />
					</Route>

					<Route path={PATHS.INVOICE_CHECKER} element={<InvoiceChecker />}>
						<Route path={PATHS.RECONCILIATION} element={<ReconciliationList />} />
						<Route path={PATHS.CLOSED_SHIPMENTS} element={<ClosedShipmentList />} />
						<Route path={PATHS.WEIGHT_CORR_OPEN} element={<WeightCorrOpen />} />
						<Route path={PATHS.WEIGHT_CORR_CLOSED} element={<WeightCorrClosed />} />
					</Route>

					<Route path={PATHS.PROMOTIONS} element={<Promotions />}>
						<Route path={PATHS.PROMO_CODES} element={<PromoCodeList />} />
						<Route path={PATHS.PROMO_TRANSACTIONS} element={<PromoTransactionsList />} />
					</Route>

					<Route path={PATHS.INVOICES} element={<Invoices />}>
						<Route path={PATHS.INVOICE} element={<Invoice />} />
						<Route path={PATHS.INVOICE_DETAILS} element={<InvoiceDetail />} />
						<Route path={PATHS.NEW_RECORDS} element={<NewRecord />} />
						<Route path={PATHS.PRICE_NOT_FOUND_ORDERS} element={<PriceNotFoundOrders />} />
						<Route path={PATHS.NOT_INVOICED_ORDERS} element={<NotInvoicedOrders />} />
					</Route>
					<Route path={PATHS.DELIVERY_COMPANIES} element={<DeliveryCompanies />} />
					<Route path={PATHS.HOME_PAGE_ANNOUNCEMENTS_MANAGEMENT} element={<HomePageAnnouncements />} />

					{/* OPERATION USER END*/}

					{/* SUPPORT MANAGER */}
					<Route path={PATHS.CLIENT_USERS} element={<UsersSupportList />} />
					<Route path={PATHS.ADDRESS_MANAGEMENT} element={<AddressManagement />}>
						<Route path={PATHS.COUNTRIES} element={<CountryList />} />
						<Route path={PATHS.STATES} element={<StateList />} />
						<Route path={PATHS.GOVERNORATES} element={<GovernorateList />} />
						<Route path={PATHS.CITIES} element={<CityList />} />
						<Route path={PATHS.DISTRICTS} element={<DistrictList />} />
						<Route path={PATHS.NEIGHBORHOODS} element={<NeighbourhoodList />} />
					</Route>
					<Route path={PATHS.DC_PICKUP_LOCATION} element={<DCPickupLocationList />} />

					<Route path={PATHS.DC_STATUS_MANAGEMENT} element={<DCStatusManagement />}>
						<Route path={PATHS.DC_STATUS_MAPPING_LIST} element={<DCStatusMappingList />} />
						<Route path={PATHS.DC_STATUSES} element={<OtoOrderStatusesList />} />
						<Route path={PATHS.DC_STATUS_GROUPS} element={<StatusGroupsList />} />
						<Route path={PATHS.STATUS_GROUP_TRANSITIONS} element={<StatusGroupTransitions />} />
					</Route>

					<Route path={PATHS.CREATE_SHIPMENT_ERROR_MAPPING} element={<CreateShipmentErrorMapping />}>
						<Route
							path={PATHS.CREATE_SHIPMENT_ERROR_MAPPING_LIST}
							element={<CreateShipmentErrorMappingList />}
						/>
						<Route path={PATHS.SHIPMENT_ERROR_LOGS_BY_AI} element={<ShipmentErrorLogsByAiList />} />
					</Route>

					<Route path={PATHS.MISSING_MAPPING_STATUSES} element={<MissingMappngStatuses />}>
						<Route
							path={PATHS.MISSING_MAPPING_STATUSES_GROUPED}
							element={<MissingMappingStatusesGroupedList />}
						/>
						<Route
							path={PATHS.MISSING_MAPPING_STATUSES_DETAILED}
							element={<MissingMappingStatusesDetailedList />}
						/>
					</Route>

					{/* BRAND MANAGER */}
					<Route path={PATHS.BM_ORDERS} element={<BrandOrders />} />
					<Route path={PATHS.BM_PRODUCTS} element={<BMProductList />} />
					<Route path={PATHS.BM_INVENTORY} element={<BMInventoryList />} />
					{/* ADMIN */}
					<Route path={PATHS.ACCESS_CONTROL} element={<AccessControl />} />
					<Route path={PATHS.ROLE_TREE} element={<RoleTree />} />
					<Route path={PATHS.ENUM_MANAGEMENT} element={<EnumManagement />} />
					<Route path={PATHS.STORY_MANAGEMENT} element={<StoryManagementList />} />
					<Route path={PATHS.LANGUAGE_RESOURCE_MANAGEMENT} element={<LanguageResourceManagement />} />
					<Route path={PATHS.CLIENT_LISTING} element={<ClientListing />} />
					<Route path={PATHS.OTHER_INTEGRATIONS_LIST} element={<OtherIntegrationsList />} />
					<Route path={PATHS.SUBSCRIPTION} element={<Subscriptions />}>
						<Route path={PATHS.SUBSCRIPTION_LISTING} element={<SubscriptionListing />} />
						<Route
							path={PATHS.SUBSCRIPTION_TRANSACTIONS_LISTING}
							element={<SubscriptionTransactionsListing />}
						/>
					</Route>
					<Route path={PATHS.PACKAGES} element={<Packages />}>
						<Route path={PATHS.PACKAGES_LIST} element={<PackagesList />} />
						<Route path={PATHS.MODULES} element={<ModulesList />} />
					</Route>
					<Route path={PATHS.ADMIN_SALES_CHANNELS} element={<SalesChannelsList />} />
					<Route path={PATHS.ADMIN_COUNTRIES_MANAGEMENT} element={<CountriesManagementList />} />
					<Route path={PATHS.COHORT_ANALYSIS} element={<CohortAnalysis />}>
						<Route path={PATHS.COHORT_AGGREGATOR_ANALYSIS} element={<CohortAggregatorAnalysis />} />
						<Route path={PATHS.COHORT_INTEGRATOR_ANALYSIS} element={<CohortIntegratorAnalysis />} />
					</Route>

					<Route path={PATHS.DELIVERY_COMPANY_MODULE} element={<DeliveryCompanyModule />}>
						<Route path={PATHS.DELIVERY_COMPANY_SETTINGS} element={<DeliveryCompanySettings />} />
						<Route path={PATHS.POLYGON_COVERAGE} element={<PolygonCoverage />} />
					</Route>
					<Route path={PATHS.DELIVERY_COMPANY_SETTINGS_PICKUP_CITIES} element={<PickupCities />} />

					{/* GENERAL */}
					<Route path={PATHS.BASE} element={<MainPage />} />
					<Route path={PATHS.ERROR} element={<ErrorPage />} />
					{/* FINANCE */}
					<Route path={PATHS.BANK_TRANSFER} element={<BankTransferList />} />
					<Route path={PATHS.OTO_LINK_FINANCE_TRANSACTIONS} element={<OTOLinkTransactionList />} />
					<Route path={PATHS.TRANSACTIONS} element={<Transactions />}>
						<Route path={PATHS.CLIENT_TRANSACTIONS} element={<ClientTransactionList />} />
						<Route path={PATHS.REFUND_TRANSACTIONS} element={<RefundTransactionsList />} />
					</Route>
					<Route path={PATHS.BANKS} element={<Banks />} />

					<Route path={PATHS.COD_RECEIVABLES} element={<CODReceivables />}>
						<Route path={PATHS.NEW_RECEIVABLE_REPORTS} element={<NewReceivableReports />} />
						<Route path={PATHS.NO_CONFLICT} element={<NoConflict />} />
						<Route path={PATHS.RECEIVED_OTO_BANK_FROM_DC} element={<ReceivedOTOBankFromDC />} />
						<Route path={PATHS.WAITING_RECONCILATION_ORDERS} element={<WaitingReconciliationOrders />} />
						<Route path={PATHS.RECONCILED_ORDERS} element={<ReconciledOrders />} />
					</Route>

					<Route path={PATHS.OTO_INVOICE_LOGS} element={<OTOInvoiceLogs />}>
						<Route path={PATHS.INVOICE_LOGS} element={<InvoiceLogs />} />
					</Route>

					<Route path={PATHS.COD_PAYABLE} element={<CODPayable />}>
						<Route path={PATHS.NEW_COD_REPORTS} element={<NewCODReportsList />} />
						<Route path={PATHS.APPROVED_COD_REPORTS} element={<ApprovedCODReportList />} />
						<Route path={PATHS.CASH_DEPOSITED_REPORTS} element={<CashDepositedReportList />} />
					</Route>

					<Route path={PATHS.COD_WALLET_TRANSACTIONS} element={<CODWalletTransactions />}>
						<Route path={PATHS.BANK_TRANSFER_FAILED} element={<BankTransferFailedList />} />
						<Route path={PATHS.BANK_TRANSFER_PROCESSING} element={<BankTransferProcessingList />} />
						<Route path={PATHS.ALL_WITHDRAWALS} element={<AllWithdrawalsList />} />
					</Route>
					<Route path={PATHS.EXTRA_CREDITS} element={<ExtraCredits />}>
						<Route path={PATHS.EXTRA_CREDITS_SMB} element={<ExtraCreditForSmb />} />
						<Route path={PATHS.EXTRA_CREDITS_ENTERPISE} element={<ExtraCreditForEnterprise />} />
					</Route>
					<Route path={PATHS.SAVINGS_CALCULATOR} element={<SavingsCalculator />} />
				</Route>
				<Route path={PATHS.OTOAWB} element={<OTOAWB />} />
				{/* Public Routes */}
				<Route element={<PublicRoutes />}>
					<Route path={PATHS.LOGIN} element={<Login />}>
						<Route path=":lang" element={null} />
					</Route>
					<Route path={PATHS.REGISTER} element={<Register />}>
						<Route path=":lang" element={null} />
					</Route>
					<Route path={PATHS.RESET_PASSWORD} element={<ChangePassword />}>
						<Route path=":lang" element={null} />
					</Route>
					<Route path={PATHS.REGISTER_WITH_PROVIDER} element={<RegisterWithProvider />} />
					<Route path={PATHS.FORGOT_PASSWORD} element={<ForgotPassword />} />
				</Route>
				<Route path={PATHS.TERMS_AND_CONDITIONS} element={<TermsAndConditions />}>
					<Route path=":lang" element={null} />
				</Route>
				<Route path={PATHS.LOGOUT} element={<Logout />} />
				<Route path={PATHS.ALL_ROUTES} element={<PageNotFound />} />
			</Routes>
		</Suspense>
	);
};

export default Router;
